import { axios } from '@ben/api/axios';
import type { IFile } from '@ben/orion/file-upload-lists';
import type {
  IEmployeeBenefit,
  IModularBenefitPrice,
} from '../../types/modular-benefit';
import type { UnenrollData } from '../../types/enrollments';
import type { ModularBenefitUploadedFileResponse } from '@ben/api/gen/codegen.schemas';

export const getBenefit = async (
  benefitId: number
): Promise<IEmployeeBenefit> => {
  // the direct descendants of values and userInput need to be snake case, as they are un-transformed value keys in the component
  const response = await axios.get(`modular-benefits/${benefitId}/`, {
    convertCaseShallowIgnoreKeys: ['values', 'userInput'],
  });
  return response?.data as IEmployeeBenefit;
};

export const getBenefitPrice = async (
  benefitId: number,
  renewalVersionId?: number,
  data?: unknown,
  signal?: AbortSignal
): Promise<IModularBenefitPrice> => {
  const versionSuffix = renewalVersionId ? `versions/${renewalVersionId}/` : '';
  const response = await axios.post(
    `modular-benefits/${benefitId}/${versionSuffix}price/`,
    data,
    { signal }
  );
  // The API response structure matches IModularBenefitPrice, cast for component compatibility
  return response.data as IModularBenefitPrice;
};

export const getCalculatedValue = async (
  benefitId: number,
  fieldId: number,
  data: unknown,
  signal?: AbortSignal
): Promise<unknown> => {
  const response = await axios.post(
    `/modular-benefits/${benefitId}/calculated-value-field/${fieldId}/recalculate/`,
    data,
    { signal }
  );
  return response?.data;
};

export const enrollBenefit = async (
  benefitId: number,
  data: unknown
): Promise<unknown> => {
  const response = await axios.patch(
    `modular-benefits/${benefitId}/enrolments/enroll/`,
    data
  );
  return response?.data;
};

export const unenrollBenefit = async (
  benefitId: number,
  data?: UnenrollData
): Promise<void> => {
  await axios.patch(`modular-benefits/${benefitId}/enrolments/unenroll/`, data);
};

export const updateBenefit = async (
  benefitId: number,
  data: unknown
): Promise<void> => {
  await axios.patch(`modular-benefits/${benefitId}/enrolments/update/`, data);
};

export const setContributionSource = async (
  id: number,
  data: unknown
): Promise<void> => {
  await axios.post(`benefits-enrolment/${id}/contribution-sources/`, data);
};

export const listBenefitDocuments = async (
  benefitId: number,
  fieldId: number
): Promise<ModularBenefitUploadedFileResponse[]> => {
  const response = await axios.get<ModularBenefitUploadedFileResponse[]>(
    `modular-benefits/${benefitId}/fields/${fieldId}/files/`
  );
  return response.data;
};

export const uploadBenefitDocument = async (
  files: File[],
  benefitId: number,
  fieldId: number
): Promise<IFile[]> => {
  const responses: IFile[] = [];
  for (const file of files) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(
      `modular-benefits/${benefitId}/fields/${fieldId}/files/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    responses.push(response?.data as IFile);
  }

  return responses;
};

export const deleteBenefitDocument = async (
  uuid: string,
  benefitId: number,
  fieldId: number
): Promise<void> => {
  await axios.delete(
    `modular-benefits/${benefitId}/fields/${fieldId}/files/${uuid}/`
  );
};

export const setWaiveBenefitReason = async (
  benefitId: number,
  optOutReasonCode: string
): Promise<void> => {
  await axios.patch(`modular-benefits/${benefitId}/enrolments/opt-out/`, {
    optOutReasonCode: optOutReasonCode,
  });
};
